/**
 * TaskListPage — tasks grouped by status with filter bar
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter } from 'lucide-react';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { getAllTasks, getMyTasks } from '@/services/tmService';
import type { Task, TaskStatus, TaskPriority } from '@/types/tm.types';
import {
  TASK_STATUS_LABELS, TASK_STATUS_DOT,
  TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS, TASK_STATUSES,
} from '@/types/tm.types';
import { cn } from '@/utils/cn';

function daysUntil(date: Date | null) {
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
}

export default function TaskListPage() {
  const { tmUser, isTMAdmin } = useTMAuth();
  const [tasks,     setTasks]     = useState<Task[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState<'all' | 'mine'>('all');
  const [priority,  setPriority]  = useState<TaskPriority | 'all'>('all');

  useEffect(() => {
    if (!tmUser) return;
    const load = async () => {
      setLoading(true);
      const data = (isTMAdmin && filter === 'all')
        ? await getAllTasks()
        : await getMyTasks(tmUser.uid);
      setTasks(data);
      setLoading(false);
    };
    load();
  }, [tmUser, isTMAdmin, filter]);

  const filtered = tasks.filter(t =>
    priority === 'all' || t.priority === priority
  );

  const byStatus = (status: TaskStatus) => filtered.filter(t => t.status === status);

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Tasks</h1>
        {isTMAdmin && (
          <Link to="/tm/tasks/new"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold active:scale-95 transition-all">
            <Plus className="h-4 w-4" /> New
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {isTMAdmin && (
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
            {(['all', 'mine'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn('px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize',
                  filter === f ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')}>
                {f === 'all' ? 'All tasks' : 'My tasks'}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-gray-400" />
          <select
            value={priority}
            onChange={e => setPriority(e.target.value as TaskPriority | 'all')}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="all">All priorities</option>
            {(['urgent', 'high', 'medium', 'low'] as TaskPriority[]).map(p => (
              <option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Status columns */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-6">
          {TASK_STATUSES.map(status => {
            const list = byStatus(status);
            if (list.length === 0) return null;
            return (
              <div key={status}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={cn('w-2.5 h-2.5 rounded-full', TASK_STATUS_DOT[status])} />
                  <h2 className="text-sm font-semibold text-gray-700">{TASK_STATUS_LABELS[status]}</h2>
                  <span className="ml-auto text-xs text-gray-400 font-medium">{list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.map(task => {
                    const d = daysUntil(task.dueDate);
                    return (
                      <Link key={task.id} to={`/tm/tasks/${task.id}`}
                        className="block bg-white rounded-2xl border border-gray-100 shadow-sm p-4 active:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-semibold text-gray-900 leading-snug">{task.title}</p>
                          <span className={cn('shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full', TASK_PRIORITY_COLORS[task.priority])}>
                            {TASK_PRIORITY_LABELS[task.priority]}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-xs text-gray-500 line-clamp-2 mb-2">{task.description}</p>
                        )}
                        <div className="flex items-center gap-3">
                          {task.assignedTo && (
                            <span className="text-[10px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                              {task.assignedTo.name.split(' ')[0]}
                            </span>
                          )}
                          {d !== null && (
                            <span className={cn('text-[10px] font-medium',
                              d < 0 ? 'text-red-500' : d <= 3 ? 'text-amber-600' : 'text-gray-400')}>
                              {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : `${d}d left`}
                            </span>
                          )}
                          {task.progress > 0 && (
                            <span className="ml-auto text-[10px] text-gray-400">{task.progress}%</span>
                          )}
                        </div>
                        {task.progress > 0 && (
                          <div className="mt-2 h-1 rounded-full bg-gray-100">
                            <div className="h-1 rounded-full bg-indigo-400 transition-all" style={{ width: `${task.progress}%` }} />
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">
              No tasks found.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
